import { ANSWER_KEY_FORMAT, type AnswerKeyItem } from '@lastrouter/contracts';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Dialog,
  DialogContent,
  Field,
  Input,
  Plus,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Trash2,
  Upload,
} from '@lastrouter/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation, useApiQuery } from '../../api/hooks.js';

/*
 * Gabarito da bateria: perguntas e a fórmula do Qlik que responde cada uma (nunca um número; a engine calcula o valor
 * esperado). Entra por planilha (Pergunta, Resposta), por arquivo JSON ou JSON colado, e se edita numa tabela com páginas.
 * O arquivo segue o formato lastrouter.gabarito/1 (docs/gabarito.md); o prompt abaixo pede esse formato a uma IA ligada ao Qlik.
 */

type Meta = { gerado_em: string | null; convencoes: string[] };

/** Prompt para uma IA com acesso ao Qlik (por MCP) montar o gabarito no formato do Lastrouter. */
export function answerKeyPrompt(appName: string, appId: string, today: string): string {
  return [
    `Você tem acesso ao Qlik Sense por ferramentas. Monte o gabarito de testes do app "${appName}" (id ${appId}).`,
    '',
    '1. Leia o modelo do app: tabelas, campos, medidas mestres, variáveis e as pastas com os gráficos que as pessoas usam.',
    '2. Escreva de 30 a 60 perguntas como alguém da área perguntaria, em português, cobrindo: totais, indicadores (percentuais e razões), tempo (um mês, acumulado, comparação entre meses), recortes pelas dimensões principais, maior e menor (top N) e metadados (data da carga, mês base).',
    '3. Para cada pergunta, escreva a fórmula do Qlik que dá a resposta certa como um valor só. Use {1} ou {1<...>} para ignorar as seleções. Use os nomes de campo exatamente como estão no app. Percentual vem como razão (0,43), sem multiplicar por 100. Se a resposta é um nome, use FirstSortedValue; vários nomes, junte com ", ".',
    '4. Calcule cada fórmula no Qlik antes de incluir. Tire as que derem erro, nulo ou um valor que você não consegue explicar.',
    '5. Marque "critico": true nas perguntas que não podem errar.',
    '6. Não coloque o valor calculado no arquivo: o Lastrouter calcula de novo no Qlik.',
    '7. Responda só com o JSON abaixo, sem texto antes ou depois.',
    '',
    JSON.stringify(
      {
        formato: ANSWER_KEY_FORMAT,
        app: { nome: appName, id: appId },
        gerado_em: today,
        convencoes: ['Regra de fórmula que vale para o app inteiro'],
        testes: [{ id: 'T01', categoria: 'totais', pergunta: 'Pergunta como a pessoa faria', resposta: 'Sum({1} [Campo])', critico: false }],
      },
      null,
      2,
    ),
  ].join('\n');
}

/** O gabarito no arquivo do formato lastrouter.gabarito/1, para baixar e editar fora. */
function toFile(appName: string, appId: string, meta: Meta, items: AnswerKeyItem[]) {
  return {
    formato: ANSWER_KEY_FORMAT,
    app: { nome: appName, id: appId },
    gerado_em: meta.gerado_em,
    convencoes: meta.convencoes,
    testes: items.map((x, i) => ({ id: x.id || `T${String(i + 1).padStart(2, '0')}`, categoria: x.categoria ?? '', pergunta: x.pergunta, resposta: x.formula, critico: x.critico })),
  };
}

const PAGE = 20;

const toBase64 = (buf: ArrayBuffer) => {
  let s = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

/** Junta o que veio ao que já está na tabela; pergunta repetida fica com a fórmula nova. */
function merge(current: AnswerKeyItem[], added: AnswerKeyItem[]): AnswerKeyItem[] {
  const key = (x: AnswerKeyItem) => x.pergunta.trim().toLowerCase();
  const byKey = new Map(added.map((x) => [key(x), x]));
  return [...current.map((x) => byKey.get(key(x)) ?? x), ...added.filter((x) => !current.some((c) => key(c) === key(x)))];
}

export function AnswerKeyCard({ runId, appName, appId }: { runId: string; appName: string; appId: string }) {
  const { t } = useTranslation();
  const k = 'assets.answerKey';
  const q = useApiQuery('answerKey', { params: { id: runId } });
  const save = useApiMutation('saveAnswerKey', ['contextRun', 'contextRuns', 'answerKey']);
  const parse = useApiMutation('parseAnswerKey');
  const [items, setItems] = useState<AnswerKeyItem[]>([]);
  const [meta, setMeta] = useState<Meta>({ gerado_em: null, convencoes: [] });
  const [page, setPage] = useState(0);
  const [pasting, setPasting] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const file = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!q.data) return;
    setItems(q.data.items);
    setMeta({ gerado_em: q.data.gerado_em, convencoes: q.data.convencoes });
  }, [q.data]);

  const valid = items.filter((x) => x.pergunta.trim() && x.formula.trim());
  const dirty = q.data ? JSON.stringify([items, meta]) !== JSON.stringify([q.data.items, { gerado_em: q.data.gerado_em, convencoes: q.data.convencoes }]) : false;
  const prompt = answerKeyPrompt(appName, appId, new Date().toISOString().slice(0, 10));
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(toFile(appName, appId, meta, valid), null, 2)], { type: 'application/json' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: `gabarito-${appName.replace(/[^\p{L}\p{N}]+/gu, '-').toLowerCase()}.json` });
    a.click();
    URL.revokeObjectURL(url);
  };
  const pages = Math.max(1, Math.ceil(items.length / PAGE));
  const at = Math.min(page, pages - 1);
  const shown = items.slice(at * PAGE, at * PAGE + PAGE);
  const set = (i: number, patch: Partial<AnswerKeyItem>) => setItems((cur) => cur.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const load = (body: { xlsx_base64: string } | { json: string }) =>
    parse.mutate(
      { body },
      {
        onSuccess: (r) => {
          setItems((cur) => merge(cur, r.items));
          // O arquivo no formato traz data e convenções; planilha e lista solta não mexem nelas.
          if (r.gerado_em || r.convencoes.length) setMeta({ gerado_em: r.gerado_em, convencoes: r.convencoes });
          setWarnings(r.warnings);
          setPasting(null);
          toast.success(t(`${k}.loaded`, { count: r.items.length }));
        },
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );

  const pick = async (f: File) => {
    const buf = await f.arrayBuffer();
    if (/\.json$/i.test(f.name) || f.type === 'application/json') load({ json: new TextDecoder().decode(buf) });
    else load({ xlsx_base64: toBase64(buf) });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`${k}.title`)}</CardTitle>
        <CardDescription>{t(`${k}.help`)}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <input
            ref={file}
            type="file"
            accept=".xlsx,.json,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void pick(f);
              e.target.value = '';
            }}
          />
          <Button onClick={() => file.current?.click()} disabled={parse.isPending}>
            <Upload aria-hidden="true" />
            {t(`${k}.import`)}
          </Button>
          <Button onClick={() => setPasting('')} disabled={parse.isPending}>
            {t(`${k}.paste`)}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setItems((cur) => [...cur, { pergunta: '', formula: '', critico: false }]);
              setPage(Math.floor(items.length / PAGE));
            }}
          >
            <Plus aria-hidden="true" />
            {t(`${k}.add`)}
          </Button>
          <Button variant="ghost" onClick={download} disabled={!valid.length}>
            <Download aria-hidden="true" />
            {t(`${k}.download`)}
          </Button>
        </div>

        <details className="rounded-[var(--lr-radius-md)] border border-border p-3">
          <summary className="cursor-pointer font-medium">{t(`${k}.howTitle`)}</summary>
          <div className="mt-3 flex flex-col gap-3 text-[length:var(--lr-text-sm)]">
            <p>{t(`${k}.formatHelp`, { format: ANSWER_KEY_FORMAT })}</p>
            <Alert tone="warning" title={t(`${k}.privacyTitle`)}>
              {t(`${k}.privacy`)}
            </Alert>
            <Field id="gabarito-prompt" label={t(`${k}.prompt`)} hint={t(`${k}.promptHelp`)}>
              <Textarea rows={12} readOnly className="font-mono text-[length:var(--lr-text-xs)]" value={prompt} />
            </Field>
            <div>
              <Button
                size="sm"
                onClick={() =>
                  void navigator.clipboard.writeText(prompt).then(
                    () => toast.success(t(`${k}.copied`)),
                    () => toast.error(t(`${k}.copyFailed`)),
                  )
                }
              >
                <Copy aria-hidden="true" />
                {t(`${k}.copy`)}
              </Button>
            </div>
          </div>
        </details>

        {meta.gerado_em || meta.convencoes.length ? (
          <div className="flex flex-col gap-1 text-[length:var(--lr-text-sm)] text-fg-secondary">
            {meta.gerado_em ? <p>{t(`${k}.generatedAt`, { date: meta.gerado_em })}</p> : null}
            {meta.convencoes.length ? (
              <>
                <p className="font-medium text-fg">{t(`${k}.conventions`)}</p>
                <ul className="list-disc pl-5">
                  {meta.convencoes.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}
        {warnings.length ? (
          <Alert tone="warning" title={t(`${k}.warnings`, { count: warnings.length })}>
            <ul className="list-disc pl-4">
              {warnings.slice(0, 10).map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Alert>
        ) : null}

        {items.length === 0 ? (
          <p className="text-fg-secondary">{t(`${k}.empty`)}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableCaption>{t(`${k}.title`)}</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-36">{t(`${k}.category`)}</TableHead>
                    <TableHead className="w-[40%]">{t(`${k}.question`)}</TableHead>
                    <TableHead>{t(`${k}.formula`)}</TableHead>
                    <TableHead className="w-20">{t(`${k}.critical`)}</TableHead>
                    <TableHead className="w-12">
                      <span className="sr-only">{t('common.actions')}</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.map((x, n) => {
                    const i = at * PAGE + n;
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          <Input aria-label={t(`${k}.categoryN`, { n: i + 1 })} value={x.categoria ?? ''} maxLength={60} onChange={(e) => set(i, { categoria: e.target.value })} />
                        </TableCell>
                        <TableCell>
                          <Input aria-label={t(`${k}.questionN`, { n: i + 1 })} value={x.pergunta} maxLength={500} onChange={(e) => set(i, { pergunta: e.target.value })} />
                        </TableCell>
                        <TableCell>
                          <Input
                            aria-label={t(`${k}.formulaN`, { n: i + 1 })}
                            className="font-mono"
                            value={x.formula}
                            maxLength={8000}
                            placeholder="Sum(Valor)"
                            onChange={(e) => set(i, { formula: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox aria-label={t(`${k}.criticalN`, { n: i + 1 })} checked={x.critico} onChange={(e) => set(i, { critico: e.target.checked })} />
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" aria-label={t(`${k}.removeN`, { n: i + 1 })} onClick={() => setItems((cur) => cur.filter((_, j) => j !== i))}>
                            <Trash2 aria-hidden="true" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {pages > 1 ? (
              <nav aria-label={t(`${k}.pagination`)} className="flex flex-wrap items-center justify-end gap-2">
                <Button size="sm" disabled={at === 0} onClick={() => setPage(at - 1)}>
                  <ChevronLeft aria-hidden="true" />
                  {t('apps.prev')}
                </Button>
                <span className="tabular text-[length:var(--lr-text-sm)]">{t('apps.pageOf', { page: at + 1, pages })}</span>
                <Button size="sm" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>
                  {t('apps.next')}
                  <ChevronRight aria-hidden="true" />
                </Button>
              </nav>
            ) : null}
          </>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="primary"
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate(
                { params: { id: runId }, body: { items: valid, ...meta } },
                {
                  onSuccess: () => {
                    setWarnings([]);
                    toast.success(t(`${k}.saved`, { count: valid.length }));
                  },
                  onError: (err) => toast.error(errorMessage(t, err)),
                },
              )
            }
          >
            {save.isPending ? t(`${k}.saving`) : t('common.save')}
          </Button>
          <span className="text-[length:var(--lr-text-sm)] text-fg-secondary">
            {items.length > valid.length ? t(`${k}.incomplete`, { count: items.length - valid.length }) : t(`${k}.count`, { count: valid.length })}
          </span>
        </div>
      </CardContent>

      {pasting !== null ? (
        <Dialog open onOpenChange={(o) => !o && setPasting(null)}>
          <DialogContent title={t(`${k}.pasteTitle`)} description={t(`${k}.pasteHelp`)} closeLabel={t('common.close')}>
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                load({ json: pasting });
              }}
            >
              <Field id="gabarito-json" label="JSON">
                <Textarea rows={10} className="font-mono" value={pasting} onChange={(e) => setPasting(e.target.value)} placeholder={'[{"pergunta": "Quantos chamados abertos?", "resposta": "Count({<Status-={\'Closed\'}>} %CaseId)"}]'} autoFocus />
              </Field>
              <div className="flex justify-end gap-2">
                <Button onClick={() => setPasting(null)}>{t('common.cancel')}</Button>
                <Button type="submit" variant="primary" disabled={!pasting.trim() || parse.isPending}>
                  {t(`${k}.pasteLoad`)}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      ) : null}
    </Card>
  );
}
